import { useEffect, useRef, useState } from 'react'
import { daylightRemaining, resolveScreenFeatures, sunTimes } from '@skyweather/weather-core'
import { ApiError, createLiveLoader } from './api.js'
import { fieldName, localTime, localWhen, zuluTime } from './format.js'
import { FieldReport } from './FieldReport.js'
import { FitStage } from './FitStage.js'
import { ObservationsHeader } from './ObservationsHeader.js'
import { OwnerLink } from './OwnerLink.js'
import { ForecastPanel } from './ForecastPanel.js'
import { WindsAloftPanel } from './WindsAloftPanel.js'
import { linkReminder } from './link.js'
import { managePath } from '../route.js'
import { brandLogo, brandStyle, logoNeedsPlate, useBrandFont } from './brand.js'
import { chooseFeed } from './feed.js'
import { SourcePanel } from './SourcePanel.js'
import { WindPanel } from './WindPanel.js'
import { BUILD_ID, checkedText, fetchServedBuildId, holdScreenAwake, reloadReason, VERSION_CHECK_MS } from './keepalive.js'
import { screenCheckMs } from './refresh.js'
import type { KioskData } from './types.js'

/** After a failed check, wait at least this long before the next, so an outage is not hammered. */
const RETRY_FLOOR_MS = 15_000

/** Gets the screen's data. `prev` is what is on screen, so a loader can answer "unchanged" cheaply. */
type Load = (now: number, prev: KioskData | null) => Promise<KioskData>

interface Props {
  /** How to get the data: the live API, or the demo set. */
  load: Load
  /** Shown in the corner when the data is not live. */
  demo?: boolean
}

/**
 * The hangar TV. Checks for new readings as often as the site's setting says (as fast as possible by
 * default; see `refresh.ts`); the clock and every "N min ago" tick every second. It keeps the display awake,
 * checks again the moment it is shown after being hidden, and reloads itself onto a newer build when one is
 * deployed (`keepalive.ts`), so a screen left on all day never sleeps or runs stale code.
 * If SkyWeather cannot be reached, the last data stays on screen with a banner saying since when, so a
 * dropped connection never blanks the hangar, and never looks like calm weather.
 */
export function KioskScreen({ load, demo = false }: Props) {
  const [data, setData] = useState<KioskData | null>(null)
  const [error, setError] = useState<{ message: string; since: number } | null>(null)
  const [fatal, setFatal] = useState<string | null>(null)
  const [now, setNow] = useState(() => Date.now())
  /** When the screen last heard from SkyWeather, changed or not: the visible proof that it is still checking. */
  const [lastCheckAt, setLastCheckAt] = useState<number | null>(null)
  const loadRef = useRef(load)
  loadRef.current = load
  const reachableRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | undefined
    let current: KioskData | null = null
    // A chain of timeouts, not an interval: the delay comes from the site's own setting, which can change
    // at any full reload, and a slow answer never overlaps the next check.
    const refresh = async () => {
      let delay = screenCheckMs(current?.site.screenRefreshS)
      try {
        const d = await loadRef.current(Date.now(), current)
        if (cancelled) return
        current = d
        setData(d)
        if (d.offlineSince !== undefined) {
          // From the service worker's memory: show the last readings under the offline banner, from when they were saved.
          const since = d.offlineSince
          setError((prev) => prev ?? { message: 'offline', since })
          setLastCheckAt(since)
          reachableRef.current = false
          delay = Math.max(delay, RETRY_FLOOR_MS)
        } else {
          setError(null)
          setLastCheckAt(Date.now())
          reachableRef.current = true
          delay = screenCheckMs(d.site.screenRefreshS)
        }
      } catch (e) {
        if (cancelled) return
        reachableRef.current = false
        delay = Math.max(delay, RETRY_FLOOR_MS)
        if (e instanceof ApiError && (e.status === 401 || e.status === 404)) {
          inFlight.stopped = true
          setFatal(
            e.code === 'signed_out'
              ? 'Signed out. Sign in as an owner to see this screen.'
              : e.code === 'token_expired'
              ? 'This screen link has expired. Ask the site owner to renew it.'
              : e.status === 401
              ? 'This screen link is not valid or has been switched off. Ask the site owner for a new hangar-screen link.'
              : 'This screen link points at a site that does not exist or that the link does not cover.',
          )
          return
        }
        setError((prev) => prev ?? { message: e instanceof Error ? e.message : String(e), since: Date.now() })
      }
      if (!cancelled && !inFlight.stopped) timer = setTimeout(() => void run(), delay)
    }
    // One check at a time: `run` is the only caller of `refresh`, and a check asked for while one is running
    // (the page coming back into view) waits for it instead of starting a second chain.
    const inFlight = { busy: false, again: false, stopped: false }
    const run = async () => {
      if (cancelled || inFlight.stopped) return
      if (inFlight.busy) {
        inFlight.again = true
        return
      }
      inFlight.busy = true
      if (timer !== undefined) clearTimeout(timer)
      timer = undefined
      try {
        await refresh()
      } finally {
        inFlight.busy = false
      }
      if (inFlight.again && !cancelled) {
        inFlight.again = false
        void run()
      }
    }
    void run()
    const tick = setInterval(() => setNow(Date.now()), 1_000)
    // A hidden page's timers are slowed or frozen by the browser; when the screen is shown again, check now
    // rather than whenever the throttled timer gets round to it.
    const onVisible = () => {
      if (document.visibilityState === 'visible') void run()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      cancelled = true
      inFlight.stopped = true
      if (timer !== undefined) clearTimeout(timer)
      clearInterval(tick)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [])

  // A real hangar screen (not the demo): keep the display awake, and move onto a newer build when one is
  // deployed, or once a day, but only while the weather is flowing so a reload never blanks the screen.
  useEffect(() => {
    if (demo) return
    const release = holdScreenAwake()
    const openedAt = Date.now()
    const check = async () => {
      const served = await fetchServedBuildId()
      if (reloadReason({ servedBuildId: served, runningBuildId: BUILD_ID, openedAt, now: Date.now(), apiReachable: reachableRef.current })) {
        window.location.reload()
      }
    }
    const versionTimer = setInterval(() => void check(), VERSION_CHECK_MS)
    return () => {
      release()
      clearInterval(versionTimer)
    }
  }, [demo])

  useBrandFont(data?.brand?.font)

  if (fatal) return <main className="kiosk kiosk--message"><p>{fatal}</p><OwnerLink /></main>
  if (!data) return <main className="kiosk kiosk--message"><p>{error ? `Can't reach SkyWeather yet (${error.message}). Retrying every 15 seconds.` : 'Loading weather…'}</p><OwnerLink /></main>

  const { site } = data
  // What the owner has switched on (weather-core `screen-features.ts`); an older server's missing field
  // means every default, which is the screen as it was before the setting existed.
  const show = resolveScreenFeatures(site.screenFeatures)
  let daylight: string | null = null
  try {
    const localDate = new Intl.DateTimeFormat('en-CA', { timeZone: site.timezone }).format(now)
    const times = sunTimes(site.lat, site.lon, localDate)
    const rem = daylightRemaining(times, now)
    if (times.sunset !== null) {
      const left = rem.remainingS === null ? '' : rem.remainingS > 0 ? ` · daylight ${Math.floor(rem.remainingS / 3600)} h ${Math.floor((rem.remainingS % 3600) / 60)} min` : ' · after sunset'
      daylight = `Sunset ${localTime(times.sunset, site.timezone)}${left}`
    }
  } catch {
    daylight = null
  }

  const feed = chooseFeed(data.readings)
  const shown = feed.shown
  const preferred = feed.preferred

  return (
    <FitStage style={brandStyle(data.brand)} branded={data.brand != null}>
      <header className="kiosk__head">
        <div className="kiosk__site">
          {brandLogo(data.brand) && (
            <span className={`kiosk__logo${logoNeedsPlate(data.brand) ? ' kiosk__logo--plate' : ''}`}>
              <img src={brandLogo(data.brand) ?? ''} alt="" />
            </span>
          )}
          <h1><a className="kiosk__home" href={managePath(demo)}>{site.name}</a></h1>
          <span>
            {[fieldName(site.field), demo ? 'DEMO DATA' : null].filter(Boolean).join(' · ')}
          </span>
        </div>
        <div className="kiosk__tools">
          <div className="kiosk__clock">
            {show.clock && <span className="kiosk__local">{localTime(now, site.timezone)}</span>}
            {show.clock && <span className="kiosk__zulu">local · {zuluTime(now)}</span>}
            <span className="kiosk__checked">{checkedText(lastCheckAt, now)}</span>
          </div>
          <OwnerLink />
        </div>
      </header>

      {linkReminder(site.linkExpiresAt, now, site.timezone) && (
        <p className="kiosk__renew" role="status">
          {linkReminder(site.linkExpiresAt, now, site.timezone)}
        </p>
      )}

      {error && (
        <p className="kiosk__offline" role="status">
          Can't reach SkyWeather since {localTime(error.since, site.timezone)}. Showing what was last received; every age below keeps counting.
        </p>
      )}

      {!shown && <p className="panel__empty">This site has no weather sources yet.</p>}
      {shown && (
        <>
          <ObservationsHeader reading={shown} trend={data.trends[shown.descriptor.id] ?? []} timeZone={site.timezone} now={now} show={show} />
          {feed.fallback && preferred && (
            <p className="kiosk__fallback" role="status">
              {preferred.descriptor.label} {preferred.observation ? `has not reported since ${localWhen(preferred.observation.observedAt, now, site.timezone)}` : 'has not reported yet'}: showing {shown.descriptor.label} instead.
            </p>
          )}
          {feed.allDown && (
            <p className="kiosk__fallback kiosk__fallback--down" role="status">
              No weather source is reporting. Showing the last reading received.
            </p>
          )}
          <div className={`kiosk__main${show.windsock || show.recentGusts ? '' : ' kiosk__main--single'}`}>
            <SourcePanel
              reading={shown}
              limits={data.limits.filter((l) => l.sourceId === shown.descriptor.id)}
              timeZone={site.timezone}
              trend={data.trends[shown.descriptor.id] ?? []}
              units={site.units}
              now={now}
              show={show}
            />
            {(show.windsock || show.recentGusts) && (
              <WindPanel
                observation={shown.observation}
                recent={data.trends[shown.descriptor.id] ?? []}
                now={now}
                units={site.units}
                stale={shown.health === 'not_reporting'}
                showSock={show.windsock}
                showGusts={show.recentGusts}
              />
            )}
          </div>
        </>
      )}

      {((show.forecast && (data.forecast?.length ?? 0) > 0) || (show.windsAloft && (data.windsAloft?.length ?? 0) > 0)) && (
        <div className={`kiosk__fcst${show.forecast && show.windsAloft && (data.forecast?.length ?? 0) > 0 && (data.windsAloft?.length ?? 0) > 0 ? ' kiosk__fcst--two' : ''}`}>
          {show.forecast && <ForecastPanel items={data.forecast ?? []} units={site.units} timeZone={site.timezone} now={now} />}
          {show.windsAloft && <WindsAloftPanel items={data.windsAloft ?? []} units={site.units} timeZone={site.timezone} now={now} elevationFt={site.elevationFt} />}
        </div>
      )}

      {show.fieldReport && (
        <FieldReport readings={data.readings} units={site.units} timeZone={site.timezone} now={now} showFlightCategory={show.flightCategory} showRemarks={show.remarks} />
      )}

      {((show.daylight && daylight) || data.brand != null) && (
        <footer className="kiosk__foot">
          {data.brand != null && <span className="kiosk__powered">Powered by SkyWeather</span>}
          {show.daylight && daylight && <span>{daylight}</span>}
        </footer>
      )}
    </FitStage>
  )
}

/** Build the live loader from the page URL: `/kiosk/<siteId>#swd_<token>`. */
export function liveLoader(siteId: string, token: string): Load {
  return createLiveLoader(siteId, token)
}
